import React, {createContext, ReactNode, useCallback, useContext, useEffect, useReducer, useState} from "react";
import {UserModel} from "../../../services/account/userModel";
import {AccountService} from "../../../services/account/accountService";
import {StorageKeys} from "../../../infrastructure/storage/storageKeys";
import { BrugerIndstillingModel } from "core/sharedmodels/brugerIndstilling/brugerIndstillingModel";
import useStamdataApi from "core/hooks/useStamdataApi";
import { BrugerIndstillingTypeKeyEnum } from "core/sharedmodels/brugerIndstilling/brugerIndstillingTypeKeyEnum";
import * as Sentry from "@sentry/react";
import { captureSentryException } from "core/infrastructure/errors/sentry/sentryUtils";

const accountService = new AccountService();
const StorageEventType = "storage";

type UserContextType = {
    user: UserModel;
    settings: BrugerIndstillingModel[];
}

const UserContext = createContext<{
    currentUser: UserContextType,
    settingsLoading: boolean,
    setUser: (payload: UserModel) => void,
    setUserSettings: (payload: BrugerIndstillingModel[]) => void,
    setUserSetting: (key: BrugerIndstillingTypeKeyEnum, value: string) => void,
} | undefined>(undefined);

enum UserReducerActionType {
    SetUser,
    SetSettings,
}

const userReducer = (
    currentUser: UserContextType,
    action: { type: UserReducerActionType, payload: Partial<UserContextType> }
): UserContextType => {
    switch (action.type) {
        case UserReducerActionType.SetUser:
            return {
                ...currentUser,
                user: action.payload.user ?? currentUser.user
            }
        case UserReducerActionType.SetSettings:
            return {
                ...currentUser,
                settings: action.payload.settings ?? currentUser.settings
            }
    }
}

export const useUserContext = () => {
    const context = useContext(UserContext);
    if (!context) throw new Error("useUserContext must be used in UserContextProvider");
    return context;
};

type UserContextProviderProps = { children?: ReactNode; }

const UserContextProvider = ({children}: UserContextProviderProps) => {
    const [currentUser, dispatch] = useReducer(userReducer, {user: accountService.getUser(), settings: []});
    const { stamdataBrugerIndstillingApi } = useStamdataApi();
    const [ settingsLoading, setSettingsLoading ] = useState(true);

    const setUser = useCallback((payload: UserModel) => {
        dispatch({
            type: UserReducerActionType.SetUser,
            payload: { user: UserModel.CreateUser(payload) }
        });
    }, []);

    const setUserSettings = useCallback((payload: BrugerIndstillingModel[]) => {
        dispatch({
            type: UserReducerActionType.SetSettings,
            payload: { settings: payload }
        });
    }, []);

    const setUserSetting = useCallback((key: BrugerIndstillingTypeKeyEnum, value: string) => {
        dispatch({
            type: UserReducerActionType.SetSettings,
            payload: { settings: [ ...currentUser.settings.filter(x => x.typeKey !== key), { typeKey: key, value: value } ] }
        });
    }, [currentUser.settings]);

    useEffect(() => {
        const onLocalStorageUserUpdatedListener = (ev: StorageEvent) => {
            if (ev.key === StorageKeys.user) {
                const updatedUser = accountService.getUser();
                setUser(updatedUser);
            }
        }

        window.addEventListener(StorageEventType, onLocalStorageUserUpdatedListener);
        return () => window.removeEventListener(StorageEventType, onLocalStorageUserUpdatedListener);
    }, [currentUser.user.UserId, setUser]);

    useEffect(() => {
        const fetchSettings = async () => {
            const userIsAuthenticated = currentUser.user.Authenticated && currentUser.user.UserId && accountService.getUser().Authenticated;

            if (userIsAuthenticated) {
                const userIsExpired = await accountService.IsUserExpired();
                if (userIsExpired) {
                    // Dont fetch brugerIndstillinger, let the rest of the app handle expiration/logout:
                    return;
                }

                setSettingsLoading(true);
                stamdataBrugerIndstillingApi
                    .GetBrugerIndstillinger()
                    .then(result => setUserSettings(result))
                    .catch((reason) => { // Make sure we never throw exception (and logout)
                        setUserSettings([]);
                        console.error(reason);
                        captureSentryException(reason, "generic-error", "fetchSettings");
                    })
                    .finally(() => setSettingsLoading(false));
            }
        }
        fetchSettings();
    }, [currentUser.user.Authenticated, currentUser.user.UserId, setUserSettings, stamdataBrugerIndstillingApi]);

    useEffect(() => {
        const updateSentryUserContext = () => {
            if (!!currentUser.user.UserId) {
                Sentry.setUser({ 
                    id: currentUser.user.UserId,
                    email: currentUser.user.Email,
                    username: currentUser.user.Name  
                });
            } else {
                Sentry.setUser(null);
            }
        }
        updateSentryUserContext();
    }, [currentUser.user.Email, currentUser.user.Name, currentUser.user.UserId]);

    return (
        <UserContext.Provider
            value={{
                    currentUser: currentUser,
                    settingsLoading,
                    setUser,
                    setUserSettings,
                    setUserSetting
                }}>
            {children}
        </UserContext.Provider>
    );
};

export default UserContextProvider;
